import { Document, Page, Text, View, StyleSheet, pdf, Font, Image } from "@react-pdf/renderer";
import _ from "lodash";

// Invoice PDF Component
export const InvoicePDF = ({ rowData }: { rowData: any }) => {
  // Format date

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  // Extract call date from the first call
  const callDate = rowData.calls && rowData.calls.length > 0
    ? formatDate(rowData.calls[0].call_start_time)
    : formatDate(rowData.requested_on);

  // Format address with comma if needed
  const formatAddress = () => {
    const holderAddress = rowData.bankAccountValue?.bank_details?.account_holder_address || "";
    const residingCountry = rowData.bankAccountValue?.bank_details?.account_holder_residing_country || "";

    if (holderAddress && residingCountry) {
      return `${holderAddress}, ${residingCountry}`;
    }
    return `${holderAddress}${residingCountry}`;
  };

  // Signature image URL
  const signatureImageUrl = "https://infollion-webapp.in-maa-1.linodeobjects.com/Finance%2FAbhishek%20Sign.png";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Enhanced Header with Logo and Invoice Title */}
        <View style={styles.header}>
          <View>
            <Text style={styles.invoiceTitle}>INVOICE</Text>
          </View>
        </View>

        {/* From and To Address with improved styling */}
        <View style={styles.addressSection}>
          <View style={styles.addressBox}>
            <Text style={styles.addressTitle}>FROM</Text>
            <Text style={styles.addressText}>
              Infollion Research Services Ltd.
              {"\n"}
              3rd Floor, Awfis, Tower B, Unitech Cyber
              {"\n"}
              Park, Gurgaon, Sec-39
              {"\n"}
              Haryana (India)
            </Text>
          </View>
          <View style={styles.addressBox}>
            <Text style={styles.addressTitle}>TO</Text>
            <Text style={styles.addressText}>
              {rowData.bankAccountValue?.account_holder_name || rowData?.expert_name}
              {"\n"}
              {formatAddress()}
            </Text>
          </View>
        </View>

        {/* Enhanced Invoice Details Section */}
        <View style={styles.detailsSection}>
          <Text style={styles.detailsTitle}>INVOICE DETAILS</Text>
          <View style={styles.detailsRow}>
            <Text style={styles.detailsLabel}>Call Date:</Text>
            <Text style={styles.detailsValue}>{callDate}</Text>
          </View>
          <View style={styles.detailsRow}>
            <Text style={styles.detailsLabel}>Call ID:</Text>
            <Text style={styles.detailsValue}>{rowData.callIds}</Text>
          </View>
        </View>

        {/* Enhanced Items Section */}
        <View style={styles.itemsSection}>
          <Text style={styles.itemsTitle}>ITEMS</Text>

          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={styles.tableCell1}>ITEM NO.</Text>
            <Text style={styles.tableCell2}>DESCRIPTION</Text>
            <Text style={styles.tableCell3}>AMOUNT</Text>
          </View>

          {/* Table Rows */}
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <Text style={styles.tableCell1}>Item 1</Text>
              <Text style={styles.tableCell2}>
                Consultation Purposes
              </Text>
              <Text style={styles.tableCell3}>
                {rowData.currency + " " + rowData.amount}
              </Text>
            </View>
          </View>
          {/* Enhanced Total Section */}
          <View style={styles.totalSection}>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>TOTAL</Text>
              <Text style={styles.totalValue}>
                {rowData.currency + " " + rowData.amount}
              </Text>
            </View>
          </View>
        </View>

        {/* Signature Section */}
        <View style={styles.signatureSection}>
          <Image
            src={signatureImageUrl}
            style={styles.signatureImage}
          />
          <Text style={styles.signatureText}>*This is a system generated document and needs no signature</Text>
        </View>
      </Page>
    </Document>
  );
};

// Register fonts for PDF
Font.register({
  family: "Roboto",
  fonts: [
    { src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf", fontWeight: 300 },
    { src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf", fontWeight: 400 },
    { src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf", fontWeight: 500 },
    { src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf", fontWeight: 700 }
  ]
});

// Enhanced styles with better typography hierarchy
const styles = StyleSheet.create({
  page: {
    padding: 50,
    fontFamily: "Roboto",
    fontSize: 10,
    color: "#333333",
    backgroundColor: "#FFFFFF",
  },
  header: {
    flexDirection: "row",
    justifyContent: "center",
    marginBottom: 40,
  },
  logoSection: {
    width: "40%",
  },
  invoiceTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: "#1976D2",
    textAlign: "right",
  },
  invoiceSubtitle: {
    fontSize: 10,
    color: "#666666",
    textAlign: "right",
    marginTop: 5,
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: "#EEEEEE",
    marginVertical: 15,
  },
  addressSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 30,
  },
  addressBox: {
    width: "45%",
  },
  addressTitle: {
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 8,
    color: "#1976D2",
  },
  addressText: {
    fontSize: 9,
    lineHeight: 1.6,
  },
  detailsSection: {
    marginBottom: 30,
    backgroundColor: "#F9F9F9",
    padding: 15,
    borderRadius: 4,
  },
  detailsTitle: {
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 10,
    color: "#1976D2",
  },
  detailsRow: {
    flexDirection: "row",
    marginBottom: 6,
  },
  detailsLabel: {
    width: "25%",
    fontSize: 9,
    fontWeight: 500,
    color: "#555555",
  },
  detailsValue: {
    width: "75%",
    fontSize: 9,
  },
  itemsSection: {
    marginBottom: 30,
  },
  itemsTitle: {
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 10,
    color: "#1976D2",
  },
  table: {
    marginTop: 10,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F0F7FF",
    paddingVertical: 8,
    paddingHorizontal: 5,
    borderBottomWidth: 1,
    borderBottomColor: "#DDDDDD",
    fontWeight: 700,
    fontSize: 9,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 10,
    paddingHorizontal: 5,
    borderBottomWidth: 1,
    borderBottomColor: "#EEEEEE",
    fontSize: 9,
  },
  tableCell1: {
    width: "15%",
  },
  tableCell2: {
    width: "60%",
  },
  tableCell3: {
    width: "25%",
    textAlign: "right",
  },
  totalSection: {
    backgroundColor: "#F9F9F9",
    padding: 15,
    marginTop: 10,
    borderRadius: 4,
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    fontWeight: 700,
    fontSize: 12,
    color: "#1976D2",
    marginTop: 5,
  },
  totalLabel: {
    width: "15%",
    textAlign: "right",
    paddingRight: 10,
  },
  totalValue: {
    width: "25%",
    textAlign: "right",
  },
  signatureSection: {
    position: "absolute",
    bottom: 50,
    right: 50,
    width: "40%",
    alignItems: "flex-end",
  },
  signatureImage: {
    width: 300,
    height: 120,
    marginBottom: 20,
    marginRight: 20,
    objectFit: "contain",
  },
  signatureText: {
    fontSize: 8,
    color: "#777777",
    textAlign: "right",
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 50,
    right: 50,
    fontSize: 8,
    color: "#777777",
    textAlign: "center",
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#EEEEEE",
  },
  footerLeft: {
    position: "absolute",
    bottom: 30,
    left: 50,
    fontSize: 8,
    color: "#777777",
  },
  footerRight: {
    position: "absolute",
    bottom: 30,
    right: 50,
    fontSize: 8,
    color: "#777777",
    textAlign: "right",
  },
  themeColor: {
    color: "#1976D2",
  }
});

export default InvoicePDF;